import React from 'react';

import { QText } from '@captivateiq/quotient';

import { getAgentConfig } from '@captivateiq/assistant/config/agentConfig';
import { useAssistantTranslation } from '../../../../contexts/AssistantContext';

type AgentIntroductionProps = {
  agentName: string;
};

// Left-to-right through the header text: near-black -> brand purple -> brand blue. Values are
// pulled directly from the Eclipse theme (utility.black, accent.a[700], accent.b[700]) — hardcoded
// here (not referenced as sx token paths) because `background-clip: text` gradients need literal
// color stops, which theme.palette lookups can't provide inline. Not a missed token migration.
const HEADLINE_GRADIENT = 'linear-gradient(100deg, #10171A 0%, #8435F2 55%, #329CCD 100%)';

// Shared between the intro (agent launch) screen and the persistent header atop an open thread —
// both show the same agent identity, so this stays in sync everywhere it's used.
export const AgentIntroduction = ({ agentName }: AgentIntroductionProps) => {
  const { t } = useAssistantTranslation();
  const config = getAgentConfig(agentName, t);

  return (
    <>
      <QText
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.onSurface.background.lowContrast',
        }}
      >
        AI Agent
      </QText>
      <QText
        sx={{
          fontSize: '32px',
          fontWeight: 600,
          lineHeight: '1.15',
          letterSpacing: '-0.025em',
          background: HEADLINE_GRADIENT,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          marginTop: '-2px',
          marginBottom: '8px',
        }}
      >
        {config.name}
      </QText>
      <QText
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          lineHeight: '20px',
          color: 'text.onSurface.background.mediumContrast',
        }}
      >
        {config.medium_description}
      </QText>
    </>
  );
};
